import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Info, Loader2 } from "lucide-react";
import { HelpPopover, HelpSection } from "@/components/common/HelpPopover";
import { RESOURCE_STATUSES, type ResourceStatus } from "@/types/admin";

/**
 * The statuses an operator may write. `Decommissioning` is left out: it is the state the delete and
 * reinstall flows run in, and ProvisioningRecoveryJob reads every VM holding it with no deletion
 * time as a reinstall whose poll was lost. Picked here for a live machine, it would be resumed as a
 * reinstall that never happened. The endpoint refuses it too (`STATUS_RESERVED`); it stays in
 * `RESOURCE_STATUSES` so a row genuinely being reinstalled still renders everywhere else.
 */
const ASSIGNABLE_STATUSES = RESOURCE_STATUSES.filter(
  (value) => value !== "Decommissioning",
);

/**
 * The fields this dialog reads. Kept to the three the table row already has, so a caller does not
 * need the full `Resource` to open it.
 */
export interface StatusTarget {
  name: string;
  type: string;
  status: ResourceStatus;
}

interface ChangeStatusDialogProps {
  /** The resource whose recorded status is being corrected; null closes the dialog. */
  resource: StatusTarget | null;

  /** Called when the dialog is dismissed. */
  onClose: () => void;

  /** Called with the chosen status when the operator submits. */
  onSubmit: (status: ResourceStatus) => void;

  /** Whether the update mutation is in flight. */
  isSubmitting?: boolean;
}

/**
 * Operator correction of the status WiCloud has on record.
 *
 * This writes the record and nothing else — no provider call, so nothing starts, stops or gets
 * suspended by it. It is for a row that drifted away from the hypervisor, typically a VM left at
 * `Provisioning` by a webhook that never arrived.
 *
 * Every status but `Decommissioning` is offered: which one the record should say is the operator's
 * decision. What the dialog does instead is explain the choices whose consequences are invisible
 * from the table — that the change carries the attached disks and networks with it, that
 * `Running`/`Stopped` are the two values the status sync job will overwrite (unless the VM carries
 * the expiry lock), and that `Terminating`/`Terminated` stamp no deletion and tell no provider.
 */
export function ChangeStatusDialog({
  resource,
  onClose,
  onSubmit,
  isSubmitting,
}: ChangeStatusDialogProps) {
  const [status, setStatus] = useState<ResourceStatus | "">("");

  // Seed the select with the resource's current status whenever a different row is picked. It is
  // only a starting point: submitting it unchanged is a real request, because the children behind
  // it may be the rows that are lagging.
  useEffect(() => {
    if (!resource) {
      return;
    }

    // A VM actually being reinstalled holds a status the select does not offer, so it seeds empty
    // and the placeholder asks for a choice rather than showing a value that cannot be submitted.
    const seed = ASSIGNABLE_STATUSES.some((value) => value === resource.status)
      ? resource.status
      : "";
    setStatus(seed);
  }, [resource]);

  const open = resource !== null;
  const isVm = resource?.type === "VirtualMachine";

  // SyncSingleVMStatusJob only reconciles VMs sitting at Running or Stopped with no `SuspendedAt`,
  // and writes whatever the provider reports. This write never touches `SuspendedAt`, so a VM the
  // expiry scan locked stays out of the sync — the warning names both outcomes, since the dialog is
  // not given the lock.
  const willBeResynced = isVm && (status === "Running" || status === "Stopped");

  // Neither tells the hypervisor anything, and neither stamps `DeletedAt`/`TerminatedAt`: the
  // cleanup job never picks the row up, and Delete resource refuses it from then on.
  const retiresTheRecord = status === "Terminating" || status === "Terminated";

  const leavesSuspended =
    resource?.status === "Suspended" && status !== "Suspended" && status !== "";

  const handleClose = () => {
    setStatus("");
    onClose();
  };

  const handleSubmit = () => {
    if (status === "") return;
    onSubmit(status);
  };

  return (
    <Dialog
      open={open}
      // Escape and the corner X both arrive here. While the write is out they are ignored: Cancel is
      // already disabled for the same reason, and a dialog dismissed mid-request left the operator
      // reopening it over a status that had not been re-read yet.
      onOpenChange={(next) => !next && !isSubmitting && handleClose()}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Change status</DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <DialogDescription className="break-words">
            {resource?.name} — currently {resource?.status}. This corrects what
            WiCloud has on record; the machine itself is not touched, so nothing
            is started, stopped or suspended by it.
          </DialogDescription>
          <div className="space-y-2">
            <Label htmlFor="resource-status">New status</Label>
            <Select
              value={status}
              onValueChange={(value: string) =>
                setStatus(value as ResourceStatus)
              }
              disabled={isSubmitting}
            >
              <SelectTrigger id="resource-status" className="cursor-pointer">
                <SelectValue placeholder="Select a status" />
              </SelectTrigger>
              <SelectContent>
                {ASSIGNABLE_STATUSES.map((value) => (
                  <SelectItem
                    key={value}
                    value={value}
                    className="cursor-pointer"
                  >
                    {value}
                    {value === resource?.status && " (current)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isVm && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                The disks and networks attached to this VM move to the same
                status. Anything shared with another VM is left alone.
              </AlertDescription>
            </Alert>
          )}

          {willBeResynced && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                The status sync (every 5 minutes) writes the hypervisor&rsquo;s
                answer back over Running or Stopped if they disagree. A VM
                locked by the expiry scan is skipped by the sync: it keeps what
                you write, and if its term has ended the scan suspends it again
                within about 15 minutes.
              </AlertDescription>
            </Alert>
          )}

          {retiresTheRecord && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                The provider is not told, so a machine that still runs is left
                on the hypervisor. No deletion is recorded either: the cleanup
                job never removes the row, the billing cycle is not touched, no
                job ever moves a Terminating row on, and Delete resource refuses
                the row from then on. Use Delete resource unless the machine is
                already gone.
              </AlertDescription>
            </Alert>
          )}

          {leavesSuspended && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This only changes the status. The machine stays as it is, and
                the expiry lock stays: if the term has ended, the expiry scan
                suspends the VM again. To lift a suspension, give the VM time
                with Change expiry date (or the customer renews), then Start it.
              </AlertDescription>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter className="gap-2">
          <ChangeStatusHelp />
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || status === ""}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The short version of `/docs/vm-actions.html#change-status`. */
function ChangeStatusHelp() {
  return (
    <HelpPopover guideHref="/docs/vm-actions.html#change-status">
      <HelpSection title="What it does">
        <p>
          Writes the status on WiCloud&rsquo;s record for this resource and the
          disks and networks only it owns, in one save, plus an admin-only audit
          row. Nobody is asked: the hypervisor is not called, and the expiry
          date, the expiry lock and the billing cycle are not touched. The
          customer sees the new status on their next load.
        </p>
      </HelpSection>
      <HelpSection title="Try these first">
        <p>
          · <b>Update resource by UUID</b> — the machine exists: it reads its
          real state, re-links it and settles the order
        </p>
        <p>
          · <b>Change expiry date</b> — the term or the suspension is wrong
        </p>
        <p>
          · <b>Delete resource</b> — the machine must go
        </p>
        <p>
          Change status is the last resort, for a row the infrastructure can no
          longer answer for, or for a disk or network row.
        </p>
      </HelpSection>
      <HelpSection title="It never">
        <p>· starts, stops, suspends or deletes the machine</p>
        <p>· clears the &ldquo;provisioning failed&rdquo; banner</p>
        <p>· offers Decommissioning — that belongs to delete and reinstall</p>
      </HelpSection>
    </HelpPopover>
  );
}
